/**
 * Users Page
 * User CRUD operations with role and reporting assignment
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Skeleton,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Add, EditOutlined as Edit, BlockOutlined as Block, CheckCircleOutline as CheckCircle, LockOutlined as Lock, Search, Send, Close, DevicesOutlined as Devices, MoreVert } from '@mui/icons-material';
import ActiveSessions from '@/components/users/ActiveSessions';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { useUserManagement } from '@/hooks/useUserManagement';
import type { User } from '@/services/user-management.service';
import StatusChip from '@/components/ui/StatusChip';
import PhoneField from '@/components/ui/PhoneField';
import { roleLabel } from '@/utils/roleLabel.util';
import { field, useUrlListState } from '@/hooks/admin/useUrlListState';

/** Rows per request — matches the backend's own default page size. */
/** Row-action icon button: muted until hovered, then tinted by intent. */
const quietAction = (hover: string) => ({
  color: 'text.disabled',
  '& .MuiSvgIcon-root': { fontSize: 18 },
  '&:hover': { color: hover, backgroundColor: 'action.hover' },
});

const PAGE_SIZE = 20;

/** Role keys are server-defined (tenants can add custom roles), so this stays a
 *  free string — the backend validates, and an unknown value matches nothing. */
const USERS_URL_SPEC = {
  search: field.str(),
  role: field.str(),
  page: field.pageIndex(),
};

const Users: React.FC = () => {
  const {
    users,
    usersTotal,
    usersLoading,
    loadUserDirectory,
    roles,
    invites,
    loading,
    error,
    success,
    loadUsers,
    loadRoles,
    loadInvites,
    createInvite,
    revokeInvite,
    resendInvite,
    updateUser,
    setUserStatus,
    clearMessages,
  } = useUserManagement();

  const { state: query, patch: patchQuery } = useUrlListState(USERS_URL_SPEC);

  // Skeleton only while the users PAGE itself is being fetched — `usersLoading`
  // (never set by a cache hit), not the shared `loading`, so sending an invite
  // or loading roles doesn't blank the table. Before the first read settles we
  // also show it, so the first frame isn't "No users found".
  const [hasFetched, setHasFetched] = useState(false);
  const showLoader = usersLoading || !hasFetched;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionsFor, setSessionsFor] = useState<User | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<User | null>(null);
  // Open state and target row are separate ON PURPOSE: MUI keeps rendering the
  // menu's items through its ~200ms fade-out after close. Clearing the target on
  // close made them re-render with no user mid-fade ("Disable" flashed into a
  // green "Enable"). Closing only drops the anchor; the target is replaced on
  // the next open.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuUser, setMenuUser] = useState<User | null>(null);
  const openRowMenu = (anchor: HTMLElement, user: User) => {
    setMenuUser(user);
    setMenuAnchor(anchor);
  };
  const closeRowMenu = () => setMenuAnchor(null);
  // Close first, then act on the row the menu was opened for.
  const runRowAction = (action: (user: User) => void) => {
    const target = menuUser;
    closeRowMenu();
    if (target) action(target);
  };
  // Other members' sessions: owner and workspace admins only (backend enforces too).
  // Read from the store, not useAuth(): that hook (un)registers the global logout
  // callback on mount/unmount, so leaving this page would clear it.
  const viewerRole = useSelector((state: RootState) => state.auth.user?.role);
  const canManageSessions = viewerRole === 'tenant_owner' || viewerRole === 'tenant_admin';
  // Every write on this page (add/invite, edit, disable, resend/revoke invite) is a
  // userManagement 'edit' action on the backend; 'read' only lists. Show controls the
  // viewer can actually use instead of letting them hit a 403 on submit.
  const { hasPermission } = usePermissionAccess();
  const canEditUsers = hasPermission('userManagement', 'edit');
  const showActions = canEditUsers || canManageSessions;
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState(query.search);
  // Search and role are answered by the server now (they have to be — the list
  // is paginated, and filtering only the twenty rows in hand would miss every
  // match on another page). `searchTerm` is debounced before it becomes a
  // request so typing doesn't fire one per keystroke.
  const debouncedSearch = query.search;
  const roleFilter = query.role;
  const setRoleFilter = (value: string) => patchQuery({ role: value });
  const page = query.page; // 0-indexed for TablePagination
  const setPage = (value: number) => patchQuery({ page: value });
  // Everyone in the tenant, for the "Reports To" picker only. That list is a
  // tenant-wide choice: it must not narrow to whoever happens to match the
  // table's current search, or to the page being viewed.
  const [allUsers, setAllUsers] = useState<User[]>([]);
  // Client-side format check only — "does this email already exist" is a
  // server-side question (see handleSubmit's catch), never guessable here.
  const [emailError, setEmailError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: '',
    roleId: '',
    reportingTo: '',
  });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    loadRoles();
    loadInvites();
  }, [loadRoles, loadInvites]);

  // Tenant-wide list for the "Reports To" picker — loaded when a dialog opens,
  // not on every mount (it used to download up to 500 users each time this tab
  // appeared). Cached; the hook drops it whenever a change affects it.
  const loadPicker = () => {
    loadUserDirectory()
      .then(setAllUsers)
      .catch(() => setAllUsers([]));
  };

  // The page reset rides along with the debounced value so both land in one
  // render — resetting on keystroke would fire a throwaway fetch of page 1
  // with the OLD search before the debounce settles.
  useEffect(() => {
    // The page reset now comes from the hook, which knows which field is the
    // page — and which correctly leaves a deep-linked page alone when the
    // debounce fires on mount with the value it just read out of the URL.
    const timer = setTimeout(() => patchQuery({ search: searchTerm.trim() }), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // One request per (page, search, role) combination. The page is reset to 0 by
  // the filter handlers themselves rather than by an effect, so a filter change
  // and its page reset land in the same render — no throwaway fetch of page 4
  // of a result set that no longer has one.
  useEffect(() => {
    loadUsers({
      page: page + 1,
      limit: PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(roleFilter ? { role: roleFilter } : {}),
    })
      .catch(() => {
        // error state set by the hook
      })
      .finally(() => setHasFetched(true));
  }, [loadUsers, page, debouncedSearch, roleFilter]);

  // Deep-linked from a "request to join" email (see auth.service.ts#requestToJoin
  // on the backend) — someone at this company's domain asked to be added.
  // Pre-fills and opens the invite dialog so the admin doesn't have to retype
  // the requester's email.
  useEffect(() => {
    const inviteEmail = searchParams.get('inviteEmail');
    if (!inviteEmail) return;
    setFormData((prev) => ({ ...prev, email: inviteEmail }));
    setEditMode(false);
    setSelectedUser(null);
    setEmailError('');
    loadPicker();
    setDialogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCreate = () => {
    // tenant_admin is the only assignable role right now (tenant_user isn't
    // wired to any backend route yet — see role-sync.util.ts's module
    // comment) — pre-select it since it's also the only dropdown option.
    const tenantAdminRole = roles.find((r) => r.key === 'tenant_admin');
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      role: '',
      roleId: tenantAdminRole?._id || '',
      reportingTo: '',
    });
    setEditMode(false);
    setSelectedUser(null);
    setEmailError('');
    loadPicker();
    setDialogOpen(true);
    clearMessages();
  };

  const handleOpenEdit = (user: User) => {
    // Prevent editing tenant_owner
    if (user.role === 'tenant_owner') {
      return;
    }
    setFormData({
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phoneNumber: user.phoneNumber || '',
      role: user.role,
      roleId: user.roleId?._id || '',
      reportingTo: user.reportingTo?._id || '',
    });
    setEditMode(true);
    setSelectedUser(user);
    setEmailError('');
    loadPicker();
    setDialogOpen(true);
    clearMessages();
  };

  // Optimistic (see the hook): the row flips now, the request confirms, and a
  // failure rolls the row back and shows the error — so the confirm dialog can
  // close immediately instead of waiting on the network.
  const applyStatus = async (user: User, isActive: boolean) => {
    setConfirmDisable(null);
    try {
      await setUserStatus(user._id, isActive);
      setTimeout(() => clearMessages(), 3000);
    } catch (err) {
      // Error + rollback handled by the hook
    }
  };

  // Enable is immediate; Disable confirms first — it also signs them out everywhere.
  const handleToggleStatus = (user: User) => {
    if (user.role === 'tenant_owner') return; // backend refuses too
    if (user.isActive) setConfirmDisable(user);
    else void applyStatus(user, true);
  };

  const handleSubmit = async () => {
    if (!editMode) {
      const trimmedEmail = formData.email.trim();
      if (!/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(trimmedEmail)) {
        setEmailError('Enter a valid email address');
        return;
      }
    }
    setEmailError('');

    try {
      if (editMode && selectedUser) {
        const userData: any = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber || undefined,
        };
        if (formData.roleId) {
          userData.roleId = formData.roleId;
        } else if (formData.role) {
          userData.role = formData.role;
        }
        if (formData.reportingTo) {
          userData.reportingTo = formData.reportingTo;
        }
        await updateUser(selectedUser._id, userData);
      } else {
        // Invite, not create — the invitee sets their own name/password when
        // they accept (see InviteAccept.tsx). Nothing here sets a password.
        const payload: any = { email: formData.email };
        if (formData.roleId) {
          payload.roleId = formData.roleId;
        } else if (formData.role) {
          payload.role = formData.role;
        }
        if (formData.reportingTo) {
          payload.reportingTo = formData.reportingTo;
        }
        await createInvite(payload);
      }

      setDialogOpen(false);
      setTimeout(() => clearMessages(), 3000);
    } catch (err) {
      // Error handled by hook
    }
  };

  // The explicit handleDelete is no longer used, as we swapped to handleToggleStatus

  // `users` is already the filtered page the server returned (same fields the
  // old client-side filter matched on: first name, last name, email, role), so
  // re-filtering here would only ever contradict the pager's count.

  const availableUsers = allUsers.filter((u) => !selectedUser || u._id !== selectedUser._id);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>
          {success}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" gap={2} flex={1}>
          <TextField
            placeholder="Search users..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Role</InputLabel>
            <Select
              value={roleFilter}
              label="Filter by Role"
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="tenant_owner">{roleLabel('tenant_owner')}</MenuItem>
              <MenuItem value="tenant_admin">{roleLabel('tenant_admin')}</MenuItem>
              <MenuItem value="tenant_user">{roleLabel('tenant_user')}</MenuItem>
              {roles
                .filter((role) => role.key !== 'tenant_owner')
                .map((role) => (
                  <MenuItem key={role._id} value={role.key}>
                    {roleLabel(role.name)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
        {canEditUsers && (
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
            Add User
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          {showLoader ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Reports To</TableCell>
                    <TableCell>Status</TableCell>
                    {showActions && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton width="70%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="85%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rounded" width={84} height={22} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="50%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rounded" width={64} height={22} />
                      </TableCell>
                      {showActions && (
                        <TableCell>
                          <Skeleton variant="circular" width={22} height={22} />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Reports To</TableCell>
                    <TableCell>Status</TableCell>
                    {showActions && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showActions ? 6 : 5} align="center">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={roleLabel(user.roleId?.name || user.role)}
                              size="small"
                              color={user.role === 'tenant_owner' ? 'primary' : 'default'}
                              {...(user.role === 'tenant_owner'
                                ? { icon: <Lock sx={{ fontSize: '12px !important' }} /> }
                                : {})}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.reportingTo
                            ? `${user.reportingTo.firstName} ${user.reportingTo.lastName}`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            status={user.isActive ? 'active' : 'inactive'}
                            label={user.isActive ? 'Active' : 'Inactive'}
                            dot
                          />
                        </TableCell>
                        {showActions && (
                          <TableCell>
                            <Tooltip
                              title={user.role === 'tenant_owner' ? 'The owner cannot be modified' : 'Actions'}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Actions for ${user.firstName} ${user.lastName}`}
                                  aria-haspopup="menu"
                                  onClick={(e) => openRowMenu(e.currentTarget, user)}
                                  disabled={user.role === 'tenant_owner'}
                                  sx={quietAction('primary.main')}
                                >
                                  <MoreVert />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!showLoader && usersTotal > 0 && (
            <TablePagination
              component="div"
              count={usersTotal}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          )}
        </CardContent>
      </Card>

      {(invites.length > 0) && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Pending Invites
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Expires</TableCell>
                    {canEditUsers && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell>{invite.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={roleLabel(
                            roles.find((r) => r._id === invite.role)?.name || invite.role
                          )}
                          size="small"
                          color={invite.role === 'tenant_admin' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{new Date(invite.expiresAt).toLocaleDateString()}</TableCell>
                      {canEditUsers && (
                        <TableCell align="right">
                          <Tooltip title="Resend invite">
                            <IconButton size="small" onClick={() => resendInvite(invite.id)}>
                              <Send fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revoke invite">
                            <IconButton size="small" onClick={() => revokeInvite(invite.id)}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Add (invite) / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            {/* The page-level Alert above is hidden behind this modal — surface
                the same error here too so a failed submit is never silent. */}
            {error && (
              <Alert severity="error" onClose={clearMessages}>
                {error}
              </Alert>
            )}
            {!editMode && (
              <Typography variant="body2" color="text.secondary">
                We'll email them a link to join — they'll set their own name and password.
              </Typography>
            )}
            {editMode && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                  fullWidth
                />
              </Box>
            )}
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (emailError) setEmailError('');
              }}
              required
              fullWidth
              disabled={editMode}
              autoComplete="off"
              inputProps={{ autoComplete: 'off' }}
              error={!!emailError}
              helperText={emailError || undefined}
            />
            {/* No password field: an admin setting a member's password is an
                account takeover — members reset their own (backend refuses it).
                No status field either: Disable / Enable in the row menu is the
                one way, and it also signs the member out. */}
            {editMode && (
              <PhoneField
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(digits) => setFormData({ ...formData, phoneNumber: digits })}
                defaultCountry="in"
                size="small"
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.roleId}
                label="Role"
                onChange={(e) => setFormData({ ...formData, roleId: e.target.value, role: '' })}
              >
                {/* Every real, assignable role: tenant_admin plus any
                    tenant-defined custom role — now that requirePermission
                    actually enforces Role.permissions on the backend, a
                    custom role grants real access instead of silently doing
                    nothing. tenant_owner is excluded (singleton, set once at
                    tenant creation); tenant_user is excluded too — it isn't
                    wired to any backend route yet, so inviting someone as
                    tenant_user would leave them locked out of the whole
                    product (see role-sync.util.ts's module comment).
                    Re-include it here once that's fixed. Also keep whatever
                    role is currently selected (e.g. editing a pre-existing
                    tenant_user from before this restriction) so the dropdown
                    shows their real role instead of going blank. */}
                {roles
                  .filter(
                    (role) =>
                      (role.key !== 'tenant_owner' && role.key !== 'tenant_user' && role.isActive) ||
                      role._id === formData.roleId
                  )
                  .map((role) => (
                    <MenuItem key={role._id} value={role._id}>
                      {roleLabel(role.name)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Reports To</InputLabel>
              <Select
                value={formData.reportingTo}
                label="Reports To"
                onChange={(e) => setFormData({ ...formData, reportingTo: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {availableUsers.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || (!editMode && (!formData.email || !formData.roleId))}
          >
            {loading ? <CircularProgress size={20} /> : editMode ? 'Update' : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Row actions — one shared menu for the row whose ⋮ was clicked */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {canEditUsers && (
          <MenuItem onClick={() => runRowAction(handleOpenEdit)}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canEditUsers && (
          <MenuItem
            onClick={() => runRowAction(handleToggleStatus)}
            sx={{ color: menuUser?.isActive ? 'error.main' : 'success.main' }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {menuUser?.isActive ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{menuUser?.isActive ? 'Disable' : 'Enable'}</ListItemText>
          </MenuItem>
        )}
        {/* Owner and workspace admins only — the backend enforces the same rule. */}
        {canManageSessions && (
          <MenuItem onClick={() => runRowAction(setSessionsFor)}>
            <ListItemIcon>
              <Devices fontSize="small" />
            </ListItemIcon>
            <ListItemText>Signed-in devices</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Disable confirmation */}
      <Dialog open={!!confirmDisable} onClose={() => !loading && setConfirmDisable(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Disable {confirmDisable?.firstName} {confirmDisable?.lastName}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            They're signed out of every device immediately and can't sign in until enabled again.
            Their data is kept.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisable(null)} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={loading}
            onClick={() => confirmDisable && applyStatus(confirmDisable, false)}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Block />}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signed-in devices of a member */}
      <Dialog open={!!sessionsFor} onClose={() => setSessionsFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Signed-in devices — {sessionsFor?.firstName} {sessionsFor?.lastName}
        </DialogTitle>
        <DialogContent>
          {sessionsFor && <ActiveSessions mode="member" userId={sessionsFor._id} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionsFor(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Legacy Delete Confirmation Dialog (Hidden/Unused) */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedUser?.firstName} {selectedUser?.lastName}? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
